import { z } from "zod";
import { DateTimeFilterSchema } from "../utils/index.js";
import { zodQueryStringArray } from "../utils/schema.js";
import {
  type FactEdgeMetadata,
  type FactNodeContent,
  FactNodeSchema,
} from "./FactNode.js";
import { NoteFactNodeContentSchema } from "./FactNodes/NoteFactNode.js";
import {
  PersonEmailReadSchema,
  PersonEmailSchema,
  PersonFactNodeContentSchema,
  PersonKnowsFactEdgeMetadataSchema,
  PersonWorksAtFactEdgeMetadataSchema,
} from "./FactNodes/PersonFactNode.js";
import {
  noteBodyMaxLength,
  noteTitleMaxLength,
  NoteV2Schema,
} from "./NoteV2.js";
import {
  PaginationMetaResponseSchema,
  PaginationQuerySchema,
} from "./Pagination.js";

export enum ContactCacheKeys {
  NetworkActivity = "network-activity",
}

export const contactCsvRequiredHeaders = ["name"] as const;

export const contactImportFileMaxSize = 5 * 1024 ** 2; // 5 MB

export const contactCsvOptionalHeaders = [
  "alias",
  "email",
  "phone",
  "website",
  "linkedin",
  "company",
  "role",
  "memo",
  "memo2",
  "memo3",
  "memo4",
  "memo5",
  "memo6",
  "memo7",
  "memo8",
  "memo9",
  "memo10",
] as const;

export const contactCsvMemoColumns = contactCsvOptionalHeaders.filter(
  (
    h,
  ): h is Extract<
    (typeof contactCsvOptionalHeaders)[number],
    `memo${string}`
  > => h.startsWith("memo"),
);

export type ContactCsvRow = Record<
  (typeof contactCsvRequiredHeaders)[number],
  string
> &
  Partial<Record<(typeof contactCsvOptionalHeaders)[number], string>>;

export const contactMetadata = {
  nodeType: PersonFactNodeContentSchema.shape.nodeType.value,
  nodeVersion: PersonFactNodeContentSchema.shape.version.value,
  knowsContactEdgeType: PersonKnowsFactEdgeMetadataSchema.shape.edgeType.value,
  knowsContactEdgeVersion:
    PersonKnowsFactEdgeMetadataSchema.shape.version.value,
  knowsContactEdgeRelationship: "contact",
  worksAtEdgeType: PersonWorksAtFactEdgeMetadataSchema.shape.edgeType.value,
  worksAtEdgeVersion: PersonWorksAtFactEdgeMetadataSchema.shape.version.value,
};

export const ContactSchema = z.object({
  id: FactNodeSchema.shape.id,
  creatorId: FactNodeSchema.shape.creatorId,
  createdAt: FactNodeSchema.shape.createdAt,
  removedAt: FactNodeSchema.shape.removedAt,
  createdByAssistant: FactNodeSchema.shape.createdByAssistant,
  isPrivate: FactNodeSchema.shape.private,
  name: z.string(),
  alias: z.string().optional(),
  email: PersonEmailReadSchema,
  phoneNumber: z.string().optional(),
  website: z.string().optional(),
  linkedin: z.string().optional(),
  tags: z
    .array(
      z.object({
        id: z.string(),
        name: z.string(),
        color: z.string().optional(),
        addedAt: z.date(),
      }),
    )
    .optional(),
  notes: z
    .array(
      z.object({
        id: NoteV2Schema.shape.id,
        creatorId: NoteV2Schema.shape.creatorId,
        isPrivate: NoteV2Schema.shape.private,
        body: NoteFactNodeContentSchema.shape.body,
        title: NoteFactNodeContentSchema.shape.title,
        createdAt: NoteV2Schema.shape.createdAt,
        updatedAt: NoteV2Schema.shape.updatedAt,
      }),
    )
    .optional(),
  companyRoles: z
    .array(
      z.object({
        edgeId: z.string(),
        companyName: z.string(),
        role: z.string().optional(),
      }),
    )
    .optional(),
  tasks: z
    .array(
      z.object({
        id: z.string(),
        title: z.string(),
        completed: z.boolean(),
        dueDate: z.date().nullable(),
        status: z.string().nullable(),
      }),
    )
    .optional(),
});

export const ContactRawSchema = z.object({
  id: FactNodeSchema.shape.id,
  creator_id: FactNodeSchema.shape.creatorId,
  created_at: FactNodeSchema.shape.createdAt,
  removed_at: FactNodeSchema.shape.removedAt,
  private: FactNodeSchema.shape.private,
  node_type: FactNodeSchema.shape.nodeType,
  content: PersonFactNodeContentSchema,
  organization_id: FactNodeSchema.shape.organizationId,
  created_by_assistant: FactNodeSchema.shape.createdByAssistant,
});

export const ContactWithRelationsSchema = z.object({
  ...FactNodeSchema.shape,
  factNodeTags: z
    .array(
      z.object({
        createdAt: z.date(),
        tag: z.object({
          id: z.string(),
          name: z.string(),
          metadata: z
            .object({
              color: z.string().optional(),
            })
            .optional(),
        }),
      }),
    )
    .optional(),
  factNodeTasks: z
    .array(
      z.object({
        task: z.object({
          id: z.string(),
          title: z.string(),
          completed: z.boolean(),
          dueDate: z.date().nullable(),
          boardColumnId: z.string().nullable(),
        }),
      }),
    )
    .optional(),
  targetForNodes: z
    .array(
      z.object({
        source: FactNodeSchema,
      }),
    )
    .optional(),
  sourceForNodes: z
    .array(
      z.object({
        id: z.string(),
        target: FactNodeSchema,
        metadata: z.unknown(),
      }),
    )
    .optional(),
});

export const FilterContactParamsSchema = z.object({
  q: z.string().optional(),
  email: z.string().optional(),
  tagIds: z.array(z.uuid()).optional(),
  /** Defaults to "all", which is what the app filter has for now... */
  tagMatch: z.enum(["any", "all"]).optional(),
  createdAt: DateTimeFilterSchema.optional(),
  skip: z.int().optional(),
  take: z.int().max(500).optional(),
});

export const ContactCompanyRoleSchema = z.object({
  edgeId: z.string(),
  companyName: z.string(),
  role: z.string().optional(),
});

export const CreateContactNoteSchema = z.string().min(1).max(noteBodyMaxLength);

export const ContactNoteInputSchema = z.strictObject({
  body: CreateContactNoteSchema,
  title: z.string().min(1).max(noteTitleMaxLength).optional(),
});

/**
 * Normalizes a bare string note into `{ body }` before validating, so callers
 * that only send plain text (bulk import, older API clients) keep working
 * while the inferred type stays a single object shape instead of a union -
 * a union here breaks tRPC's input type inference once merged into the full
 * AppRouter.
 */
export const CreateContactNoteWithTitleSchema = z.preprocess(
  (val) => (typeof val === "string" ? { body: val } : val),
  ContactNoteInputSchema,
);

const ContactResponseSchema = z.object({
  id: ContactSchema.shape.id,
  createdAt: ContactSchema.shape.createdAt,
  name: ContactSchema.shape.name,
  alias: ContactSchema.shape.alias.nullable(),
  email: ContactSchema.shape.email.nullable(),
  phoneNumber: ContactSchema.shape.phoneNumber.nullable(),
  website: ContactSchema.shape.website.nullable(),
  tags: z.array(
    z.object({
      id: z.uuid(),
      name: z.string(),
      color: z.string().nullable(),
      addedAt: z.date(),
    }),
  ),
});

export const ContactMemoResponseSchema = z.object({
  id: z.string(),
  content: z.string(),
  title: z.string().optional(),
});

const ContactWithEntitiesResponseSchema = z.object({
  ...ContactResponseSchema.shape,
  /** @deprecated Use `memos` instead - kept as plain strings for existing integrations. */
  notes: z.array(CreateContactNoteSchema),
  memos: z.array(ContactMemoResponseSchema),
  companyRoles: z.array(
    z.object({
      companyName: z.string(),
      role: z.string().nullable(),
    }),
  ),
  tasks: z.array(
    z.object({
      id: z.uuid(),
      title: z.string(),
      completed: z.boolean(),
      dueDate: z.date().nullable(),
      status: z.string().nullable(),
    }),
  ),
});

export const CreateContactCompanyRoleSchema = ContactCompanyRoleSchema.pick({
  companyName: true,
  role: true,
});

export const CreateContactSchema = z.object({
  name: PersonFactNodeContentSchema.shape.name,
  alias: PersonFactNodeContentSchema.shape.alias,
  email: PersonEmailSchema,
  phoneNumber: ContactSchema.shape.phoneNumber,
  website: ContactSchema.shape.website,
  linkedin: ContactSchema.shape.linkedin,
  isPrivate: ContactSchema.shape.isPrivate.optional(),
  notes: z.array(CreateContactNoteWithTitleSchema).optional(),
  companyRoles: z.array(CreateContactCompanyRoleSchema).optional(),
  tagIds: z.array(z.uuid()).optional(),
});

export const UpdateContactCompanyRoleSchema = ContactCompanyRoleSchema.pick({
  edgeId: true,
  companyName: true,
  role: true,
});

export const UpdateContactSchema = z.object({
  name: PersonFactNodeContentSchema.shape.name,
  alias: PersonFactNodeContentSchema.shape.alias.nullish(),
  email: PersonEmailSchema.nullish(),
  phoneNumber: ContactSchema.shape.phoneNumber.nullish(),
  website: ContactSchema.shape.website.nullish(),
  linkedin: ContactSchema.shape.linkedin.nullish(),
  isPrivate: ContactSchema.shape.isPrivate.optional(),
  notes: z
    .array(
      z.object({
        id: z.string(),
        body: z.string(),
        title: z.string().max(noteTitleMaxLength).optional(),
      }),
    )
    .optional(),
  companyRoles: z.array(UpdateContactCompanyRoleSchema).optional(),
  tagIds: z.array(z.uuid()).optional(),
});

export type Contact = z.infer<typeof ContactSchema>;
export type ContactRaw = z.infer<typeof ContactRawSchema>;
export type ContactContent = Extract<FactNodeContent, { nodeType: "person" }>;
export type ContactWithRelations = z.infer<typeof ContactWithRelationsSchema>;
export type ContactWorksAtMetadata = Extract<
  FactEdgeMetadata,
  { edgeType: "worksAt" }
>;
export type KnowsContactMetadata = Extract<
  FactEdgeMetadata,
  { edgeType: "knowsPerson" }
>;

export type FilterContactParams = z.infer<typeof FilterContactParamsSchema>;
export type CreateContact = z.infer<typeof CreateContactSchema>;
export type UpdateContact = z.infer<typeof UpdateContactSchema>;

export type ContactCompanyRole = z.infer<typeof ContactCompanyRoleSchema>;
export type CreateContactCompanyRole = z.infer<
  typeof CreateContactCompanyRoleSchema
>;
export type UpdateContactCompanyRole = z.infer<
  typeof UpdateContactCompanyRoleSchema
>;

export const MemoToolInputSchema = z.union([
  z.string().min(1).max(noteBodyMaxLength),
  z.object({
    content: z.string().min(1).max(noteBodyMaxLength),
    title: z.string().min(1).max(noteTitleMaxLength).optional(),
  }),
]);
export type MemoToolInput = z.infer<typeof MemoToolInputSchema>;

export function toMemoNote(memo: MemoToolInput) {
  return typeof memo === "string"
    ? { body: memo }
    : { body: memo.content, title: memo.title };
}

const contactTargetSchema = z
  .string()
  .min(1)
  .describe("The contact's id, name or email.");

export const CreateContactToolInputSchema = z.object({
  name: z
    .string()
    .min(1)
    .max(128)
    .describe("The contact's name, usually a full name."),
  alias: z.string().max(128).optional().describe("The contact's nickname."),
  email: PersonEmailSchema,
  phoneNumber: z.string().max(128).optional(),
  website: z.string().optional(),
  linkedin: z.string().optional().describe("LinkedIn profile URL."),
  private: z
    .boolean()
    .optional()
    .describe("Only the creator can see a private contact. Defaults to false."),
  memos: z
    .array(MemoToolInputSchema)
    .optional()
    .describe(
      "Memos to store on the contact: plain text, or content with a title.",
    ),
  companyRoles: z
    .array(
      z.object({
        companyName: z
          .string()
          .min(1)
          .describe("The company the contact works at."),
        role: z.string().optional().describe("The contact's role there."),
      }),
    )
    .optional(),
});
export const AddMemosToContactToolInputSchema = z.object({
  contact: contactTargetSchema,
  memos: z.array(MemoToolInputSchema).min(1),
  private: z
    .boolean()
    .optional()
    .describe("Only the creator can see a private memo. Defaults to false."),
});
export const AddCompanyRoleToolInputSchema = z.object({
  contact: contactTargetSchema,
  companyName: z.string().min(1).describe("The company to add the contact to."),
  role: z.string().optional().describe("The contact's role there."),
});
export const AddCompanyRoleToolOutputSchema = z.object({
  contactId: z.string(),
  companyName: z.string(),
  role: z.string().nullable(),
});
export const FindContactToolInputSchema = z.object({
  query: z.string().min(1).optional().describe("A name or email address."),
  limit: z.number().int().min(1).max(50).default(10),
  offset: z.number().int().min(0).optional(),
});
export const ListMemosToolInputSchema = z.object({
  limit: z.number().int().min(1).max(100).default(50),
  offset: z.number().int().min(0).optional(),
});
export const MemoListItemToolOutputSchema = z.object({
  id: z.string(),
  title: z.string().nullable(),
  content: z.string(),
  contactId: z.string(),
  contactName: z.string().nullable(),
  createdAt: z.string(),
});
export type CreateContactToolInput = z.infer<
  typeof CreateContactToolInputSchema
>;
export type AddMemosToContactToolInput = z.infer<
  typeof AddMemosToContactToolInputSchema
>;
export type AddCompanyRoleToolInput = z.infer<
  typeof AddCompanyRoleToolInputSchema
>;
export type AddCompanyRoleToolOutput = z.infer<
  typeof AddCompanyRoleToolOutputSchema
>;
export type MemoListItemToolOutput = z.infer<
  typeof MemoListItemToolOutputSchema
>;

export function toMemoListItemToolOutput(memo: {
  id: string;
  title?: string | null;
  body: string;
  contactId: string;
  contactName?: string | null;
  createdAt: Date;
}): MemoListItemToolOutput {
  return {
    id: memo.id,
    title: memo.title ?? null,
    content: memo.body,
    contactId: memo.contactId,
    contactName: memo.contactName ?? null,
    createdAt: memo.createdAt.toISOString(),
  };
}

/**
 * ========================================
 * PUBLIC API SCHEMAS
 * ========================================
 */
export const PostContactRequestSchema = CreateContactSchema;
export const PostContactResponseSchema = z.object({
  contact: ContactWithEntitiesResponseSchema,
});
export const PatchContactRequestSchema = z.object({
  name: PersonFactNodeContentSchema.shape.name.nullish(),
  alias: PersonFactNodeContentSchema.shape.alias.nullish(),
  email: PersonEmailSchema.nullish(),
  phoneNumber: ContactSchema.shape.phoneNumber.nullish(),
  website: ContactSchema.shape.website.nullish(),
  notes: z.array(CreateContactNoteSchema).optional(),
  companyRoles: z.array(CreateContactCompanyRoleSchema).optional(),
  tagIds: z.array(z.uuid()).optional(),
});
export const PatchContactResponseSchema = z.object({
  contact: ContactWithEntitiesResponseSchema,
});
export const GetContactParamsSchema = z.object({
  contactId: z.uuid(),
});
export const GetContactResponseSchema = z.object({
  contact: ContactWithEntitiesResponseSchema,
});
export const SearchContactsQuerySchema = z.object({
  ...PaginationQuerySchema.shape,
  q: z.string().optional(),
  email: z.string().optional(),
  tag_id: zodQueryStringArray(z.uuid()),
  tag_match: z.enum(["any", "all"]).optional().default("any"),
});

export const SearchContactsResponseSchema = z.object({
  ...PaginationMetaResponseSchema.shape,
  items: z.array(ContactResponseSchema),
});

/**
 * @deprecated Use PostMemosToContactRequestSchema (`POST /contacts/{contactId}/memos`) instead.
 */
export const PostNotesToContactRequestSchema = z.object({
  notes: z.array(CreateContactNoteWithTitleSchema).min(1).max(50),
});

/**
 * @deprecated Use PostMemosToContactResponseSchema instead.
 */
export const PostNotesToContactResponseSchema = z.object({
  contact: ContactWithEntitiesResponseSchema,
});

export const PostMemosToContactRequestSchema = z.object({
  memos: z
    .array(
      z.strictObject({
        content: CreateContactNoteSchema,
        title: z.string().min(1).max(noteTitleMaxLength).optional(),
      }),
    )
    .min(1)
    .max(50),
});

export const PostMemosToContactResponseSchema = z.object({
  memos: z.array(ContactMemoResponseSchema),
});

export type PostContactRequest = z.infer<typeof PostContactRequestSchema>;
export type PostContactResponse = z.infer<typeof PostContactResponseSchema>;
export type PatchContactRequest = z.infer<typeof PatchContactRequestSchema>;
export type PatchContactResponse = z.infer<typeof PatchContactResponseSchema>;
export type GetContactParams = z.infer<typeof GetContactParamsSchema>;
export type GetContactResponse = z.infer<typeof GetContactResponseSchema>;
export type SearchContactsResponse = z.infer<
  typeof SearchContactsResponseSchema
>;
export type PostNotesToContactRequest = z.infer<
  typeof PostNotesToContactRequestSchema
>;
export type PostNotesToContactResponse = z.infer<
  typeof PostNotesToContactResponseSchema
>;
export type PostMemosToContactRequest = z.infer<
  typeof PostMemosToContactRequestSchema
>;
export type PostMemosToContactResponse = z.infer<
  typeof PostMemosToContactResponseSchema
>;
/**
 * ========================================
 * END PUBLIC API SCHEMAS
 * ========================================
 */
